import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  PlusCircle,
  List,
  Database,
  Layers,
  RefreshCcw,
  Zap,
  LogOut,
  ShieldCheck,
  User,
  BookOpen,
} from "lucide-react";
import { useAuth } from "@/config/auth";
import AdvancedModeBanner from "@/components/AdvancedModeBanner";
import { TOSS_LOGO_URL, BRAND } from "@/lib/branding";

const advancedNav = [
  { to: "/advanced", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/advanced/new", icon: PlusCircle, label: "New Assessment" },
  { to: "/advanced/assessments", icon: List, label: "Assessments" },
  { to: "/documents", icon: BookOpen, label: "Documents" },
  { to: "/advanced/benchmarks", icon: Database, label: "Product Benchmarks", adminOnly: true },
  { to: "/advanced/material-benchmarks", icon: Layers, label: "Material Benchmarks", adminOnly: true },
];

const basicNav = [
  { to: "/basic", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/basic/new", icon: PlusCircle, label: "New Assessment" },
  { to: "/basic/assessments", icon: List, label: "Assessments" },
  { to: "/documents", icon: BookOpen, label: "Documents" },
  { to: "/basic/benchmarks", icon: Database, label: "Product Benchmarks", adminOnly: true },
  { to: "/basic/material-benchmarks", icon: Layers, label: "Material Benchmarks", adminOnly: true },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();

  const inBasicRoute = location.pathname.startsWith("/basic");
  const inAdvancedRoute = location.pathname.startsWith("/advanced");
  let preferredMode = "advanced";
  if (inBasicRoute) preferredMode = "basic";
  else if (inAdvancedRoute) preferredMode = "advanced";
  else {
    try { preferredMode = localStorage.getItem("upsi_mode") || "advanced"; }
    catch (e) { preferredMode = "advanced"; }
  }
  const isBasic = preferredMode === "basic";
  const rawNav = isBasic ? basicNav : advancedNav;

  const navItems = rawNav.filter((n) => {
    if (!n.adminOnly) return true;
    if (isBasic) return true;
    return isAdmin;
  });

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-[#F8FAF8]">
      <aside className="upsi-sidebar" data-testid="sidebar">
        {/* Brand header */}
        <div className="px-5 pt-5 pb-4 border-b border-[#EEF3EE]">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 group"
            data-testid="brand-mark-sidebar"
          >
            <img
              src={TOSS_LOGO_URL}
              alt={BRAND.short}
              className="h-9 w-auto object-contain group-hover:scale-105 transition-transform"
              data-testid="toss-logo"
            />
          </button>
          <p className="mt-2 text-[9px] tracking-[0.28em] uppercase text-[#9CA9A0] font-medium">
            {BRAND.from}
          </p>

          {/* Mode badge + switch */}
          <div className="mt-4 flex items-center justify-between">
            {isBasic ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold tracking-[0.2em] bg-[#43A047] text-white">
                <Zap className="w-2.5 h-2.5" /> BASIC
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold tracking-[0.2em] bg-[#1B291D] text-white">
                <Layers className="w-2.5 h-2.5" /> ADVANCED
              </span>
            )}
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-1 text-[10px] text-[#617364] hover:text-[#43A047] font-medium tracking-[0.16em] uppercase transition-colors"
              data-testid="switch-mode-btn"
              title="Back to landing"
            >
              <RefreshCcw className="w-3 h-3" /> Switch
            </button>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `upsi-nav-item ${isActive ? "active" : ""}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {isBasic && (
          <div className="px-4 pb-3 pt-2" data-testid="sidebar-adv-cta-wrap">
            <AdvancedModeBanner compact />
          </div>
        )}

        {user && (
          <div className="px-4 py-4 border-t border-[#EEF3EE] space-y-2" data-testid="user-block">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-[#43A047]/10 flex items-center justify-center flex-shrink-0">
                {isAdmin ? (
                  <ShieldCheck className="w-4 h-4 text-[#43A047]" />
                ) : (
                  <User className="w-4 h-4 text-[#43A047]" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-[#1B291D] truncate" data-testid="user-name">
                  {user.name}
                </p>
                <p className="text-[10px] text-[#617364] truncate">{user.email}</p>
              </div>
            </div>
            {isAdmin && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-[0.18em] bg-[#1B291D] text-white">
                <ShieldCheck className="w-2.5 h-2.5" /> ADMIN
              </span>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 text-[11px] text-[#617364] hover:text-[#D32F2F] tracking-[0.16em] uppercase font-medium pt-1 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        )}

        <div className="px-4 py-3 border-t border-[#EEF3EE]">
          <p className="text-[9px] text-[#9CA9A0] tracking-[0.14em]">
            {BRAND.short} {BRAND.version} · {BRAND.domain}
          </p>
        </div>
      </aside>

      <main className="upsi-main flex-1">
        {isBasic && (
          <div className="px-6 sm:px-8 pt-6" data-testid="page-adv-banner-wrap">
            <AdvancedModeBanner />
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
